import { sectionIsEmpty } from "./sectionState.mjs";

/**
 * One sidebar section: a body the section's controls target, with a header button
 * that collapses it (`collapsible: false` for one that is always open and has no
 * header, like Place). The section hides itself while its body is empty (Overlays
 * and Layers fill in after login).
 * @param {Document} doc
 * @param {{id: string, title: string, collapsed: boolean, collapsible?: boolean,
 *   onToggle(id: string, collapsed: boolean): void}} options
 * @returns {{element: HTMLElement, body: HTMLElement}}
 */
export function createSection(
  doc,
  { id, title, collapsed, collapsible = true, onToggle },
) {
  const element = doc.createElement("section");
  element.className = "sidebar-section";
  element.id = `sidebar-${id}`;
  const body = doc.createElement("div");
  body.className = "sidebar-section-body";
  const updateEmpty = () =>
    element.classList.toggle(
      "sidebar-section--empty",
      sectionIsEmpty(body.childElementCount),
    );
  updateEmpty();
  new MutationObserver(updateEmpty).observe(body, { childList: true });
  if (!collapsible) {
    element.append(body);
    return { element, body };
  }

  const header = doc.createElement("button");
  header.type = "button";
  header.className = "sidebar-section-header";
  header.textContent = title;
  element.append(header, body);

  const setCollapsed = (value) => {
    element.classList.toggle("sidebar-section--collapsed", value);
    header.setAttribute("aria-expanded", String(!value));
  };
  setCollapsed(collapsed);
  header.addEventListener("click", () => {
    const value = !element.classList.contains("sidebar-section--collapsed");
    setCollapsed(value);
    onToggle(id, value);
  });
  return { element, body };
}
